import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixReceiptsPresetsV1ReceiptPreset from './receipts-presets-v1-receipt-preset-receipt-presets.http.js';

/** A Receipt preset is a set of customizable receipt configurations that control the information, design, and branding of receipts to align with a site's business needs. */
export interface ReceiptPreset {
  /**
   * Receipt preset ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the receipt preset is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the receipt preset.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the receipt preset was created.
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date and time the receipt preset was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Receipt preset name.
   * @maxLength 100
   */
  name?: string;
  /** Display settings. */
  displaySettings?: DisplaySettings;
  /**
   * Custom fields.
   * Values appear on the receipt in the specified order.
   */
  customFields?: CustomFields;
  /**
   * Whether this is the default receipt preset.
   * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
   * @readonly
   */
  default?: boolean;
  /** Display values. */
  displayValues?: DisplayValues;
  /**
   * Custom field data for the receipt preset.
   *
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

export interface DisplaySettings {
  /** Business display options. */
  businessDisplayOptions?: BusinessDisplayOptions;
  /** Customer display options. */
  customerDisplayOptions?: CustomerDisplayOptions;
  /** Item display options. */
  itemsDisplayOptions?: ItemsDisplayOption;
  /** Totals display options. */
  totalsDisplayOptions?: TotalsDisplayOption;
}

export interface BusinessDisplayOptions {
  /** Whether to display the business email in the receipt. */
  showBusinessEmail?: boolean;
  /** Whether to display the business phone number in the receipt. */
  showBusinessPhoneNumber?: boolean;
  /** Whether to display the business address in the receipt. */
  showBusinessAddress?: boolean;
}

export interface CustomerDisplayOptions {
  /** Whether to display the customer's email in the receipt. */
  showCustomerEmail?: boolean;
  /** Whether to display the customer's phone number in the receipt. */
  showCustomerPhoneNumber?: boolean;
  /** Whether to display the customer's shipping address in the receipt. */
  showCustomerShippingAddress?: boolean;
  /** Whether to display the customer's billing address in the receipt. */
  showCustomerBillingAddress?: boolean;
  /** Whether to display the customer's VAT ID in the receipt. */
  showCustomerVatId?: boolean;
  /** Whether to display the customer's company's name. */
  showCustomerCompanyName?: boolean;
}

export interface ItemsDisplayOption {
  /** Whether to display item descriptions in the receipt. */
  showItemsDescription?: boolean;
  /** Whether to display item taxes in the receipt. */
  showTax?: boolean;
}

export interface TotalsDisplayOption {
  /** Whether to display the tax breakdown in the receipt. */
  showTaxBreakdown?: boolean;
  /** Whether to display the item subtotal per tax breakdown in the receipt. */
  showItemSubtotalPerTaxBreakdown?: boolean;
}

export interface CustomFields {
  /**
   * Header custom fields.
   * @maxSize 4
   */
  headerCustomFields?: CustomField[];
  /**
   * Business custom fields.
   * @maxSize 4
   */
  businessCustomFields?: CustomField[];
  /**
   * Customer custom fields.
   * @maxSize 4
   */
  customerCustomFields?: CustomField[];
  /**
   * Footer custom fields.
   * @maxSize 4
   */
  footerCustomFields?: FooterCustomField[];
}

export interface CustomField {
  /**
   * Title.
   * @minLength 1
   * @maxLength 100
   */
  title?: string | null;
  /**
   * Value.
   * @maxLength 100
   */
  value?: string;
}

export interface FooterCustomField {
  /**
   * Title.
   * @minLength 1
   * @maxLength 200
   */
  title?: string;
  /**
   * Value.
   * @maxLength 5000
   */
  value?: string | null;
}

export interface DisplayValues {
  /**
   * Text override for receipt title.
   * @minLength 1
   * @maxLength 20
   */
  titleOverride?: string | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface CreateReceiptPresetRequest {
  /** Receipt preset to create. */
  receiptPreset: ReceiptPreset;
}

export interface CreateReceiptPresetResponse {
  /** Created receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface GetReceiptPresetRequest {
  /**
   * Receipt preset ID to retrieve.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface GetReceiptPresetResponse {
  /** Requested receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface GetDefaultReceiptPresetRequest {}

export interface GetDefaultReceiptPresetResponse {
  /** Default receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface UpdateReceiptPresetRequest {
  /** Receipt preset to update. May be partial. */
  receiptPreset: ReceiptPreset;
}

export interface UpdateReceiptPresetResponse {
  /** Updated receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface SetDefaultReceiptPresetRequest {
  /**
   * Receipt preset ID to set as default.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface SetDefaultReceiptPresetResponse {
  /** Default receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface DeleteReceiptPresetRequest {
  /**
   * Receipt preset ID to delete.
   * @format GUID
   */
  receiptPresetId: string;
}

export interface DeleteReceiptPresetResponse {}

export interface ListReceiptPresetsRequest {}

export interface ListReceiptPresetsResponse {
  /** List of receipt presets. */
  receiptPresets?: ReceiptPreset[];
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated receipt preset. */
  receiptPreset?: ReceiptPreset;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateReceiptPresetApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'APP_INSTALLATION_FAILED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type GetDefaultReceiptPresetApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateReceiptPresetApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type DeleteReceiptPresetApplicationErrors =
  | {
      code?: 'NO_META_SITE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'DEFAULT_RECEIPT_PRESET';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type ListReceiptPresetsApplicationErrors = {
  code?: 'NO_META_SITE';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ReceiptPresetCreatedEnvelope {
  entity: ReceiptPreset;
  metadata: EventMetadata;
}

/**
 * Triggered when a receipt preset is created.
 * @permissionScope Manage Receipts
 * @permissionScopeId SCOPE.RECEIPTS.MANAGE
 * @permissionId RECEIPTS.RECEIPT_PRESET_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.receipts.presets.v1.receipt_preset_created
 * @slug created
 */
export declare function onReceiptPresetCreated(
  handler: (event: ReceiptPresetCreatedEnvelope) => void | Promise<void>
): void;

export interface ReceiptPresetDeletedEnvelope {
  entity: ReceiptPreset;
  metadata: EventMetadata;
}

/**
 * Triggered when a receipt preset is deleted.
 * @permissionScope Manage Receipts
 * @permissionScopeId SCOPE.RECEIPTS.MANAGE
 * @permissionId RECEIPTS.RECEIPT_PRESET_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.receipts.presets.v1.receipt_preset_deleted
 * @slug deleted
 */
export declare function onReceiptPresetDeleted(
  handler: (event: ReceiptPresetDeletedEnvelope) => void | Promise<void>
): void;

export interface ReceiptPresetUpdatedEnvelope {
  entity: ReceiptPreset;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a receipt preset is updated.
 * @permissionScope Manage Receipts
 * @permissionScopeId SCOPE.RECEIPTS.MANAGE
 * @permissionId RECEIPTS.RECEIPT_PRESET_DOMAIN_EVENTS_READ
 * @webhook
 * @eventType wix.receipts.presets.v1.receipt_preset_updated
 * @slug updated
 */
export declare function onReceiptPresetUpdated(
  handler: (event: ReceiptPresetUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a receipt preset.
 * @param receiptPreset - Receipt preset to create.
 * @public
 * @requiredField receiptPreset
 * @permissionId RECEIPTS.RECEIPT_PRESET_CREATE
 * @applicableIdentity APP
 * @returns Created receipt preset.
 * @fqn wix.receipts.presets.v1.ReceiptPresets.CreateReceiptPreset
 */
export async function createReceiptPreset(
  receiptPreset: ReceiptPreset
): Promise<
  NonNullablePaths<
    ReceiptPreset,
    | `name`
    | `displaySettings.businessDisplayOptions.showBusinessEmail`
    | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `displaySettings.businessDisplayOptions.showBusinessAddress`
    | `displaySettings.customerDisplayOptions.showCustomerEmail`
    | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerVatId`
    | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `displaySettings.itemsDisplayOptions.showItemsDescription`
    | `displaySettings.itemsDisplayOptions.showTax`
    | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomFields`
    | `customFields.footerCustomFields.${number}.title`
    | `default`,
    5
  > & {
    __applicationErrorsType?: CreateReceiptPresetApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    receiptPreset: receiptPreset,
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.createReceiptPreset(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.receiptPreset!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { receiptPreset: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['receiptPreset']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a receipt preset.
 * @param receiptPresetId - Receipt preset ID to retrieve.
 * @public
 * @requiredField receiptPresetId
 * @permissionId RECEIPTS.RECEIPT_PRESET_READ
 * @applicableIdentity APP
 * @returns Requested receipt preset.
 * @fqn wix.receipts.presets.v1.ReceiptPresets.GetReceiptPreset
 */
export async function getReceiptPreset(
  receiptPresetId: string
): Promise<
  NonNullablePaths<
    ReceiptPreset,
    | `name`
    | `displaySettings.businessDisplayOptions.showBusinessEmail`
    | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `displaySettings.businessDisplayOptions.showBusinessAddress`
    | `displaySettings.customerDisplayOptions.showCustomerEmail`
    | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerVatId`
    | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `displaySettings.itemsDisplayOptions.showItemsDescription`
    | `displaySettings.itemsDisplayOptions.showTax`
    | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomFields`
    | `customFields.footerCustomFields.${number}.title`
    | `default`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    receiptPresetId: receiptPresetId,
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.getReceiptPreset(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.receiptPreset!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { receiptPresetId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['receiptPresetId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves the default receipt preset.
 *
 * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
 * @public
 * @permissionId RECEIPTS.RECEIPT_PRESET_READ
 * @applicableIdentity APP
 * @fqn wix.receipts.presets.v1.ReceiptPresets.GetDefaultReceiptPreset
 */
export async function getDefaultReceiptPreset(): Promise<
  NonNullablePaths<
    GetDefaultReceiptPresetResponse,
    | `receiptPreset.name`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `receiptPreset.customFields.headerCustomFields`
    | `receiptPreset.customFields.headerCustomFields.${number}.value`
    | `receiptPreset.customFields.businessCustomFields`
    | `receiptPreset.customFields.customerCustomFields`
    | `receiptPreset.customFields.footerCustomFields`
    | `receiptPreset.customFields.footerCustomFields.${number}.title`
    | `receiptPreset.default`,
    6
  > & {
    __applicationErrorsType?: GetDefaultReceiptPresetApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.getDefaultReceiptPreset(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a receipt preset.
 *
 *
 * Each time the receipt preset is updated,
 * `revision` increments by 1.
 * The current `revision` must be passed when updating the receipt preset.
 * This ensures you're working with the latest receipt preset
 * and prevents unintended overwrites.
 * @param _id - Receipt preset ID.
 * @public
 * @requiredField _id
 * @requiredField receiptPreset
 * @requiredField receiptPreset.revision
 * @permissionId RECEIPTS.RECEIPT_PRESET_UPDATE
 * @applicableIdentity APP
 * @returns Updated receipt preset.
 * @fqn wix.receipts.presets.v1.ReceiptPresets.UpdateReceiptPreset
 */
export async function updateReceiptPreset(
  _id: string,
  receiptPreset: NonNullablePaths<UpdateReceiptPreset, `revision`, 2>
): Promise<
  NonNullablePaths<
    ReceiptPreset,
    | `name`
    | `displaySettings.businessDisplayOptions.showBusinessEmail`
    | `displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `displaySettings.businessDisplayOptions.showBusinessAddress`
    | `displaySettings.customerDisplayOptions.showCustomerEmail`
    | `displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `displaySettings.customerDisplayOptions.showCustomerVatId`
    | `displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `displaySettings.itemsDisplayOptions.showItemsDescription`
    | `displaySettings.itemsDisplayOptions.showTax`
    | `displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `customFields.headerCustomFields`
    | `customFields.headerCustomFields.${number}.value`
    | `customFields.businessCustomFields`
    | `customFields.customerCustomFields`
    | `customFields.footerCustomFields`
    | `customFields.footerCustomFields.${number}.title`
    | `default`,
    5
  > & {
    __applicationErrorsType?: UpdateReceiptPresetApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    receiptPreset: { ...receiptPreset, id: _id },
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.updateReceiptPreset(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.receiptPreset!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { receiptPreset: '$[1]' },
        explicitPathsToArguments: { 'receiptPreset.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'receiptPreset']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateReceiptPreset {
  /**
   * Receipt preset ID.
   * @format GUID
   * @readonly
   * @immutable
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the receipt preset is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the receipt preset.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the receipt preset was created.
   * @readonly
   * @immutable
   */
  _createdDate?: Date | null;
  /**
   * Date and time the receipt preset was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Receipt preset name.
   * @maxLength 100
   */
  name?: string;
  /** Display settings. */
  displaySettings?: DisplaySettings;
  /**
   * Custom fields.
   * Values appear on the receipt in the specified order.
   */
  customFields?: CustomFields;
  /**
   * Whether this is the default receipt preset.
   * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
   * @readonly
   */
  default?: boolean;
  /** Display values. */
  displayValues?: DisplayValues;
  /**
   * Custom field data for the receipt preset.
   *
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
}

/**
 * Sets a receipt preset as default.
 *
 * The default preset is used during receipt creation when a preset isn't specified or doesn't exist.
 * @param receiptPresetId - Receipt preset ID to set as default.
 * @public
 * @requiredField receiptPresetId
 * @permissionId RECEIPTS.RECEIPT_PRESET_SET_DEFAULT
 * @applicableIdentity APP
 * @fqn wix.receipts.presets.v1.ReceiptPresets.SetDefaultReceiptPreset
 */
export async function setDefaultReceiptPreset(
  receiptPresetId: string
): Promise<
  NonNullablePaths<
    SetDefaultReceiptPresetResponse,
    | `receiptPreset.name`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `receiptPreset.customFields.headerCustomFields`
    | `receiptPreset.customFields.headerCustomFields.${number}.value`
    | `receiptPreset.customFields.businessCustomFields`
    | `receiptPreset.customFields.customerCustomFields`
    | `receiptPreset.customFields.footerCustomFields`
    | `receiptPreset.customFields.footerCustomFields.${number}.title`
    | `receiptPreset.default`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    receiptPresetId: receiptPresetId,
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.setDefaultReceiptPreset(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { receiptPresetId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['receiptPresetId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Permanently deletes a receipt preset.
 * @param receiptPresetId - Receipt preset ID to delete.
 * @public
 * @requiredField receiptPresetId
 * @permissionId RECEIPTS.RECEIPT_PRESET_DELETE
 * @applicableIdentity APP
 * @fqn wix.receipts.presets.v1.ReceiptPresets.DeleteReceiptPreset
 */
export async function deleteReceiptPreset(receiptPresetId: string): Promise<
  void & {
    __applicationErrorsType?: DeleteReceiptPresetApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    receiptPresetId: receiptPresetId,
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.deleteReceiptPreset(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { receiptPresetId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['receiptPresetId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Lists all available receipt presets.
 *
 * By default the returned receipt presets are sorted by `default` and `updatedDate` in descending order.
 * @public
 * @permissionId RECEIPTS.RECEIPT_PRESET_READ
 * @applicableIdentity APP
 * @fqn wix.receipts.presets.v1.ReceiptPresets.ListReceiptPresets
 */
export async function listReceiptPresets(): Promise<
  NonNullablePaths<
    ListReceiptPresetsResponse,
    | `receiptPresets`
    | `receiptPresets.${number}.name`
    | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessEmail`
    | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `receiptPresets.${number}.displaySettings.businessDisplayOptions.showBusinessAddress`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerEmail`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerVatId`
    | `receiptPresets.${number}.displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `receiptPresets.${number}.displaySettings.itemsDisplayOptions.showItemsDescription`
    | `receiptPresets.${number}.displaySettings.itemsDisplayOptions.showTax`
    | `receiptPresets.${number}.displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `receiptPresets.${number}.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `receiptPresets.${number}.default`,
    6
  > & {
    __applicationErrorsType?: ListReceiptPresetsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.listReceiptPresets(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the extended fields of a receipt preset, without incrementing revision.
 * @param _id - ID of the entity to update.
 * @param namespace - Identifier for the app whose extended fields are being updated.
 * @public
 * @requiredField _id
 * @requiredField namespace
 * @requiredField options
 * @requiredField options.namespaceData
 * @param options - Field options. The `namespaceData` object **must** be passed.
 * @permissionId RECEIPTS.RECEIPT_PRESET_UPDATE
 * @applicableIdentity APP
 * @fqn wix.receipts.presets.v1.ReceiptPresets.UpdateExtendedFields
 */
export async function updateExtendedFields(
  _id: string,
  namespace: string,
  options: NonNullablePaths<UpdateExtendedFieldsOptions, `namespaceData`, 2>
): Promise<
  NonNullablePaths<
    UpdateExtendedFieldsResponse,
    | `receiptPreset.name`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessEmail`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessPhoneNumber`
    | `receiptPreset.displaySettings.businessDisplayOptions.showBusinessAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerEmail`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerPhoneNumber`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerShippingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerBillingAddress`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerVatId`
    | `receiptPreset.displaySettings.customerDisplayOptions.showCustomerCompanyName`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showItemsDescription`
    | `receiptPreset.displaySettings.itemsDisplayOptions.showTax`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showTaxBreakdown`
    | `receiptPreset.displaySettings.totalsDisplayOptions.showItemSubtotalPerTaxBreakdown`
    | `receiptPreset.customFields.headerCustomFields`
    | `receiptPreset.customFields.headerCustomFields.${number}.value`
    | `receiptPreset.customFields.businessCustomFields`
    | `receiptPreset.customFields.customerCustomFields`
    | `receiptPreset.customFields.footerCustomFields`
    | `receiptPreset.customFields.footerCustomFields.${number}.title`
    | `receiptPreset.default`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    id: _id,
    namespace: namespace,
    namespaceData: options?.namespaceData,
  });

  const reqOpts =
    ambassadorWixReceiptsPresetsV1ReceiptPreset.updateExtendedFields(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          id: '$[0]',
          namespace: '$[1]',
          namespaceData: '$[2].namespaceData',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'namespace', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateExtendedFieldsOptions {
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData: Record<string, any> | null;
}
